/**
 * SessionManager - Handles session management with cross-tab synchronization
 * Features:
 * - Persistent session ID across page refreshes
 * - Cross-tab synchronization for login/logout
 * - Automatic session cleanup on expiry
 * - Device fingerprinting for security
 */

class SessionManager {
  constructor() {
    this.SESSION_KEY = 'apollo_session';
    this.AUTH_KEY = 'apollo_auth';
    this.SYNC_EVENT = 'apollo_auth_sync';
    this.SESSION_CHECK_INTERVAL = 60000; // Check every minute
    this.listeners = new Set();
    this.checkInterval = null;
    this.lastBackendCheck = 0;
    this.authState = null;
    this.SYNC_STORAGE_KEY = 'apollo_auth_event';

    this.authState = this.loadStoredAuthState();

    // Initialize session management
    this.init();
  }

  init() {
    // Listen for storage events (cross-tab communication)
    window.addEventListener('storage', this.handleStorageChange.bind(this));

    // Listen for custom sync events
    window.addEventListener(this.SYNC_EVENT, this.handleSyncEvent.bind(this));

    // Listen for page visibility changes
    document.addEventListener('visibilitychange', this.handleVisibilityChange.bind(this));

    // Start periodic session check
    // this.startSessionCheck();

    // Handle page unload
    window.addEventListener('beforeunload', this.handleBeforeUnload.bind(this));
  }

  /**
   * Generate a unique session ID
   */
  generateSessionId() {
    const timestamp = Date.now().toString(36);
    const randomPart = Math.random().toString(36).substr(2, 9);
    const browserPart = this.getBrowserFingerprint().substr(0, 6);
    return `sess_${timestamp}_${randomPart}_${browserPart}`;
  }

  /**
   * Get a simple browser fingerprint for device identification
   */
  getBrowserFingerprint() {
    const nav = window.navigator;
    const screen = window.screen;

    const fingerprint = [
      nav.userAgent,
      nav.language,
      screen.height,
      screen.width,
      screen.colorDepth,
      new Date().getTimezoneOffset(),
      nav.hardwareConcurrency || 0,
      nav.platform
    ].join('|');

    // Simple hash function
    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }

    return Math.abs(hash).toString(36);
  }

  /**
   * Get or create session ID
   */
  getSessionId() {
    let sessionData = this.getSessionData();

    if (!sessionData || !sessionData.sessionId) {
      sessionData = {
        sessionId: this.generateSessionId(),
        createdAt: Date.now(),
        lastActivity: Date.now(),
        deviceId: this.getBrowserFingerprint()
      };
      this.saveSessionData(sessionData);
    } else {
      // Update last activity
      sessionData.lastActivity = Date.now();
      this.saveSessionData(sessionData);
    }

    return sessionData.sessionId;
  }

  /**
   * Get session data from localStorage
   */
  getSessionData() {
    try {
      const data = localStorage.getItem(this.SESSION_KEY);
      return data ? JSON.parse(data) : null;
    } catch (e) {
      console.error('Error reading session data:', e);
      return null;
    }
  }

  /**
   * Save session data to localStorage
   */
  saveSessionData(data) {
    try {
      localStorage.setItem(this.SESSION_KEY, JSON.stringify(data));
    } catch (e) {
      console.error('Error saving session data:', e);
    }
  }

  /**
   * Load stored authentication state from localStorage
   */
  loadStoredAuthState() {
    try {
      const stored = localStorage.getItem(this.AUTH_KEY);
      if (!stored) {
        return null;
      }
      const parsed = JSON.parse(stored);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (error) {
      console.error('Error loading auth data:', error);
      return null;
    }
  }

  /**
   * Persist current authentication state to storage
   */
  persistAuthState() {
    try {
      if (this.authState) {
        localStorage.setItem(this.AUTH_KEY, JSON.stringify(this.authState));
      } else {
        localStorage.removeItem(this.AUTH_KEY);
      }
    } catch (error) {
      console.error('Error persisting auth data:', error);
    }
  }

  /**
   * Get authentication data
   */
  getAuthData() {
    return this.authState;
  }

  /**
   * Get the active auth token if one exists
   */
  getAuthToken() {
    return this.authState?.token || null;
  }

  /**
   * Get the stored user object if available
   */
  getCurrentUser() {
    return this.authState?.user || null;
  }

  /**
   * Build Authorization headers for authenticated requests
   */
  getAuthHeaders(additionalHeaders = {}) {
    const headers = { ...additionalHeaders };
    const token = this.getAuthToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  }

  /**
   * Save authentication data
   */
  saveAuthData(authData, broadcast = true) {
    if (authData) {
      const nextAuthState = {
        ...(this.authState || {}),
        ...authData,
        timestamp: Date.now()
      };

      if (authData.token === undefined && this.authState?.token) {
        nextAuthState.token = this.authState.token;
      }

      // Prevent unnecessary broadcasts if nothing changed
      if (JSON.stringify(this.authState) === JSON.stringify(nextAuthState)) {
        return;
      }

      this.authState = nextAuthState;
      this.persistAuthState();

      if (broadcast) {
        this.broadcastAuthChange('login', this.authState);
      }
    } else {
      if (this.authState === null) {
        return;
      }

      this.authState = null;
      this.persistAuthState();

      if (broadcast) {
        this.broadcastAuthChange('logout', null);
      }
    }
  }

  /**
   * Clear session and auth data
   */
  clearSession() {
    // Generate new session ID for next login
    const newSessionData = {
      sessionId: this.generateSessionId(),
      createdAt: Date.now(),
      lastActivity: Date.now(),
      deviceId: this.getBrowserFingerprint()
    };

    this.saveSessionData(newSessionData);
    this.saveAuthData(null);

    // Clear chat session ID if exists
    if (window.sessionStorage) {
      window.sessionStorage.removeItem('chat_session_id');
    }
  }

  /**
   * Validate current session by checking the backend using cookie-based auth
   */
  async validateSession() {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/backend/api/auth/verify`,
        {
          method: 'GET',
          credentials: 'include',
          headers: this.getAuthHeaders()
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const updates = { user: data.user };
          if (data.token) {
            updates.token = data.token;
          }
          this.saveAuthData(updates, false);
          return { valid: true, user: data.user };
        }
        return { valid: false, reason: data.error || 'unknown_error' };
      }

      if (response.status === 401) {
        return { valid: false, reason: 'unauthorized' };
      }

      console.warn(`Session validation returned status ${response.status}`);
      return { valid: false, reason: `status_${response.status}` };
    } catch (error) {
      console.error('Session validation error:', error);
      return { valid: false, reason: 'network_error' };
    }
  }

  /**
   * Broadcast authentication changes to other tabs
   */
  broadcastAuthChange(action, data) {
    // Use BroadcastChannel if available
    if (window.BroadcastChannel) {
      const channel = new BroadcastChannel('apollo_auth_channel');
      channel.postMessage({ action, data, timestamp: Date.now() });
      channel.close();
    }

    // Fallback to storage event for cross-tab communication
    try {
      const payload = {
        action,
        data,
        timestamp: Date.now()
      };
      localStorage.setItem(this.SYNC_STORAGE_KEY, JSON.stringify(payload));
      // Remove immediately to allow repeated events with same action
      localStorage.removeItem(this.SYNC_STORAGE_KEY);
    } catch (error) {
      console.error('Error broadcasting auth change via storage:', error);
    }

    // Always emit a local custom event for the current tab
    const event = new CustomEvent(this.SYNC_EVENT, {
      detail: { action, data, timestamp: Date.now() }
    });
    window.dispatchEvent(event);
  }

  /**
   * Handle storage changes from other tabs
   */
  handleStorageChange(event) {
    if (event.key === this.SYNC_STORAGE_KEY && event.newValue) {
      try {
        const payload = JSON.parse(event.newValue);
        if (payload && payload.action) {
          this.notifyListeners(payload.action, payload.data || null);
        }
      } catch (error) {
        console.error('Error parsing auth sync payload:', error);
      }
    }
  }

  /**
   * Handle custom sync events
   */
  handleSyncEvent(event) {
    const { action } = event.detail;
    this.notifyListeners(action);
  }

  /**
   * Handle page visibility changes
   */
  handleVisibilityChange() {
    if (!document.hidden) {
      // Page became visible, do a quick local validation
      this.validateSession(true).then(result => {
        if (!result.valid) {
          // Only do backend check if local validation fails
          this.validateSession(false).then(backendResult => {
            if (!backendResult.valid) {
              this.notifyListeners('session_invalid', backendResult.reason);
            }
          });
        }
      });
    }
  }

  /**
   * Handle page unload
   */
  handleBeforeUnload() {
    // Update last activity
    const sessionData = this.getSessionData();
    if (sessionData) {
      sessionData.lastActivity = Date.now();
      this.saveSessionData(sessionData);
    }
  }

  /**
   * Start periodic session check
   */
  startSessionCheck() {
    this.checkInterval = setInterval(() => {
      if (!this.authState) {
        return;
      }

      this.validateSession()
        .then(result => {
          if (!result.valid) {
            this.clearSession();
            this.notifyListeners('session_expired', result.reason);
          }
        })
        .catch(error => {
          console.error('Session validation error:', error);
        });
    }, this.SESSION_CHECK_INTERVAL);
  }

  /**
   * Stop periodic session check
   */
  stopSessionCheck() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  /**
   * Add listener for session events
   */
  addListener(callback) {
    this.listeners.add(callback);
  }

  /**
   * Remove listener
   */
  removeListener(callback) {
    this.listeners.delete(callback);
  }

  /**
   * Notify all listeners of session changes
   */
  notifyListeners(event, data = null) {
    this.listeners.forEach(callback => {
      try {
        callback(event, data);
      } catch (error) {
        console.error('Error in session listener:', error);
      }
    });
  }

  /**
   * Clean up resources
   */
  destroy() {
    this.stopSessionCheck();
    window.removeEventListener('storage', this.handleStorageChange);
    window.removeEventListener(this.SYNC_EVENT, this.handleSyncEvent);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    this.listeners.clear();
  }
}

// Export singleton instance
const sessionManager = new SessionManager();
export default sessionManager;